import { useContext, useEffect, useState } from 'react';
import { Button, Grid, TextField, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { EstateResultItem, EstateResultItem_AccountInput } from 'queries';

import { EstateResultsPageContext } from 'pages/estate-results-page/context/estate-results-page-context';

import {
  CustomResultItemField,
  handleNewCustomFieldButtonClick,
  parseCustomFieldsJson,
  removeCustomField,
  updateCustomField,
} from '../utils/custom-result-item-field';

import NewEssentialData from './new-essential-data';

type Props = {
  estateResultItem?: EstateResultItem;
  // eslint-disable-next-line @typescript-eslint/ban-types
  setEssentialData: Function;
};

const useStyles = makeStyles(() => ({
  button: {
    marginTop: '3px',
    padding: '2px 10px',
  },
  container: {
    paddingTop: '10px',
  },
  inputField: {
    textAlign: 'left',
    width: '100%',
  },
  title: {
    paddingTop: '10px',
  },
}));

export default function AddAccountItem({ setEssentialData, estateResultItem }: Props) {
  const classes = useStyles();
  const context = useContext(EstateResultsPageContext);

  const [accountInput, setAccountInput] = useState<EstateResultItem_AccountInput>({
    title: '',
    subtitle: '',
  });

  const [customFields, setCustomFields] = useState<CustomResultItemField[]>([]);

  const fields = ['Title', 'Subtitle'];
  const fieldKeys = ['title', 'subtitle'];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleInputChange = (e: any) => {
    const name: string = e.target.name;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const value: any = e.target.value;
    const newInput = { ...accountInput };
    newInput[name as keyof EstateResultItem_AccountInput] = value;
    setAccountInput(newInput);
    setEssentialData(accountInput);
  };

  const mapExistingEstateResultItemData = (estateResultItem: EstateResultItem) => {
    const essentialData = JSON.parse(estateResultItem.essentialData!);
    const newInput: EstateResultItem_AccountInput = {
      title: essentialData.title || '',
      subtitle: essentialData.subtitle || '',
      customFields: essentialData.customFields,
    };

    setAccountInput(newInput);
    setCustomFields(parseCustomFieldsJson(essentialData.customFields));
  };

  useEffect(() => {
    const essentialData: any = {};
    customFields.forEach((customField) => {
      essentialData[`customField${customField.id}`] = {
        id: customField.id,
        name: customField.field,
        value: customField.value,
      };
    });

    accountInput.customFields = essentialData;
    setEssentialData(accountInput);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customFields, accountInput]);

  useEffect(() => {
    if (estateResultItem) {
      mapExistingEstateResultItemData(estateResultItem);
    }
  }, [estateResultItem]);

  return (
    <>
      <Typography variant="h5" className={classes.title}>
        {context.isEditingEstateResultItem ? 'Edit Account' : 'Add Account'}
      </Typography>
      <Grid className={classes.container}>
        {fields.map((field, index) => {
          return (
            <Grid className={classes.container}>
              <Grid className={classes.inputField}>
                <TextField
                  className={classes.inputField}
                  fullWidth
                  name={`${fieldKeys[index]}`}
                  label={`${field}`}
                  value={
                    context.isEditingEstateResultItem
                      ? accountInput[`${fieldKeys[index] as keyof EstateResultItem_AccountInput}`]
                      : null
                  }
                  variant="outlined"
                  onChange={handleInputChange}
                  inputProps={{ maxLength: 50 }}
                />
              </Grid>
            </Grid>
          );
        })}
      </Grid>
      <Grid className={classes.container}>
        <Typography variant="h5" className={classes.title}>
          Custom Fields
        </Typography>
        <Grid item xs={12} sm={12}>
          {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            customFields.map((customField: CustomResultItemField, index: number) => {
              return (
                <NewEssentialData
                  key={index}
                  customField={customField}
                  updateCustomField={(updatedCustomField: CustomResultItemField) => {
                    updateCustomField(updatedCustomField, customFields, setCustomFields);
                  }}
                  handleRemoveClick={() => {
                    removeCustomField(customField, customFields, setCustomFields);
                  }}
                />
              );
            })
          }
        </Grid>
      </Grid>
      <Grid className={classes.container}>
        {!context.isEditingEstateResultItem && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            className={classes.button}
            onClick={() => {
              handleNewCustomFieldButtonClick(customFields, setCustomFields);
            }}
          >
            + Add new field
          </Button>
        )}
      </Grid>
    </>
  );
}
